import {
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { getAsyncStatusFromRtkMutationStates } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { conversationsApi } from 'apis/conversations';
import { PhoneNumber } from 'apis/numbers';
import { userContentApi } from 'apis/userContent';
import { Dropzone } from 'components/dropzone';
import {
  HookFormDatePicker,
  HookFormDatePickerValue,
  HookFormPhoneNumberSelect,
  HookFormQueuesSelect,
  HookFormText,
} from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import { ScheduledConversationsBanner } from '../banner/scheduledConversationsBanner';

import styles from './scheduledConversationsModal.module.scss';

type FieldValues = {
  /** The name of the list. */
  name: string;
  /** The id of the queue. */
  queueId: string[];
  /** The outbound phone number. */
  outboundNumber: [PhoneNumber];
  /** The datepicker value. */
  date: HookFormDatePickerValue;
  /** The path of the uploaded CSV file. */
  csvPath: string;
};

export const ScheduledConversationsModal = createAppModal(
  { type: 'settings:scheduledConversations' },
  ({ isOpen, hide }): JSX.Element => {
    const notify = useNotifications();

    const form = useForm<FieldValues>({ mode: 'all' });

    const [uploadCsv, uploadCsvState] =
      userContentApi.useCreateScheduledConversationsCsvMutation();

    const [
      createScheduledConversationBatchQuery,
      createScheduledConversationBatchState,
    ] = conversationsApi.useLazyCreateScheduledConversationBatchQuery();

    // Prefills the name field with the filename of the uploaded file, if the field is empty.
    useEffect(() => {
      const { status, originalArgs } = uploadCsvState;
      const values = form.getValues();

      if (status === 'fulfilled' && !values.name) {
        form.setValue('name', originalArgs?.file.name ?? '');
        form.clearErrors('name');
      }
    }, [form, uploadCsvState]);

    const handleSubmit: SubmitHandler<FieldValues> = async (
      values,
    ): Promise<void> => {
      try {
        await createScheduledConversationBatchQuery({
          ...values,
          queueId: values.queueId[0],
          outboundNumber: values.outboundNumber[0].e164_format,
          date: values.date.date ?? moment().toISOString(),
        }).unwrap();

        notify(
          {
            title: 'Success',
            text: `The conversations have been scheduled.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        ampli.scheduledConversationsCsvImported();

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while scheduling the conversations.',
          },
          NotificationType.Error,
        );
      }
    };

    const handleUpload = async (files: File[]): Promise<void> => {
      try {
        const {
          data: { relative_uri },
        } = await uploadCsv({ file: files[0] }).unwrap();

        form.setValue('csvPath', relative_uri);
      } catch {
        /**
         * No need to do anything, any error from the query is rendered in the
         * UI.
         */
      }
    };

    const fileName = uploadCsvState.originalArgs?.file?.name ?? '';

    const asyncStatusDescriptions: Record<
      typeof uploadCsvState.status,
      string
    > = {
      uninitialized: 'Drag and drop a .csv file here or click to upload',
      pending: `Uploading...`,
      fulfilled: `"${fileName}" uploaded successfully`,
      rejected: `"${fileName}" has the following errors:`,
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead
          title="Import .csv and add scheduled conversations"
          onClose={hide}
        />

        <ModalBody>
          <FlexBox flexDirection="column" gap="400">
            <ScheduledConversationsBanner />

            <div className={styles.grid}>
              <Dropzone
                accept=".csv"
                onDrop={handleUpload}
                status={uploadCsvState.status}
              >
                <FlexBox flexDirection="column" alignItems="center" gap="300">
                  <Icon name="upload" color="" size={24} />

                  <Text.Paragraph variant="3" subdued align="center">
                    {asyncStatusDescriptions[uploadCsvState.status]}
                  </Text.Paragraph>

                  {uploadCsvState.error && (
                    <FlexBox flexDirection="column">
                      {/* How to type the error response? */}
                      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                      {/* @ts-ignore */}
                      {uploadCsvState.error.data?.errors?.map((e, i) => (
                        <Text.Paragraph key={i} variant="4" subdued>
                          {e}
                        </Text.Paragraph>
                      ))}
                    </FlexBox>
                  )}
                </FlexBox>
              </Dropzone>

              <form noValidate tabIndex={-1}>
                <FormGrid>
                  <FormField>
                    <HookFormText
                      name="name"
                      label="Schedule name"
                      control={form.control}
                      rules={{
                        required: 'Schedule name is required',
                      }}
                      componentProps={{
                        autoFocus: true,
                        placeholder: 'Enter the schedule name',
                      }}
                    />
                  </FormField>

                  <FormField>
                    <HookFormQueuesSelect
                      name="queueId"
                      label="Conversations offered in..."
                      control={form.control}
                      rules={{
                        required: 'Queue is required',
                      }}
                    />
                  </FormField>

                  <FormField>
                    <HookFormPhoneNumberSelect
                      name="outboundNumber"
                      label="Conversations offered from..."
                      control={form.control}
                      rules={{
                        required: 'Phone number is required',
                      }}
                    />
                  </FormField>

                  <FormField>
                    <HookFormDatePicker
                      name="date"
                      label="Conversations offered on..."
                      control={form.control}
                      rules={{
                        required: 'Date is required',
                      }}
                      componentProps={{
                        placeholder: 'Select a date and time',
                        dateFormat: moment.localeData().longDateFormat('LLL'),
                        datePickerProps: {
                          withTimePicker: true,
                        },
                        minDate: moment().subtract(1, 'day'),
                      }}
                    />
                  </FormField>
                </FormGrid>
              </form>
            </div>
          </FlexBox>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName="Upload"
          confirmOnClick={form.handleSubmit(handleSubmit)}
          confirmProps={{
            isLoading: createScheduledConversationBatchState.isLoading,
            disabled:
              !form.formState.isValid ||
              getAsyncStatusFromRtkMutationStates(uploadCsvState) !==
                'fulfilled' ||
              form.formState.isSubmitting,
          }}
        />
      </Modal>
    );
  },
);
